import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import type { CampaignEntry } from "../state/formReducer"

/**
 * Multi-campaign chip builder. Enter key adds a chip (parity:
 * `campaigns.js:15-20`), × button removes one. Chips are the single
 * source of truth for per-campaign submission — see `FormPage` submit
 * loop.
 */

interface Props {
  campaigns: CampaignEntry[]
  input: string
  onInputChange: (value: string) => void
  onAdd: () => void
  onRemove: (id: string) => void
  error?: string
}

export function CampaignChipBuilder({
  campaigns,
  input,
  onInputChange,
  onAdd,
  onRemove,
  error,
}: Props) {
  return (
    <div data-form-field="campaigns">
      <div className={`flex gap-2 ${error ? "rounded-md border border-red-500/80 p-2" : ""}`}>
        <Input
          type="text"
          value={input}
          placeholder="Enter campaign name"
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              onAdd()
            }
          }}
        />
        <Button
          type="button"
          variant="secondary"
          onClick={onAdd}
          className="border border-[#8fa0ef]/40 bg-[#7488e8] text-white hover:bg-[#6b7edc]"
        >
          Add campaign
        </Button>
      </div>
      {error ? <p className="mt-1 text-xs text-red-400">{error}</p> : null}
      {campaigns.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {campaigns.map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-2 rounded-full border border-[#8fa0ef]/55 bg-[#4a5380] px-3 py-1 text-sm font-medium text-white"
            >
              <span>{c.name}</span>
              <button
                type="button"
                aria-label={`Remove ${c.name}`}
                className="rounded-full text-white/80 hover:text-white"
                onClick={() => onRemove(c.id)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}
